import type { ColumnVisibilityItem } from "providers/Auth";
import { moveColumn, reorderWithinSlots } from "shared/utils/column-order";

export const SECTIONS = {
  Visible: "visible",
  Hidden: "hidden"
} as const;

type Section = (typeof SECTIONS)[keyof typeof SECTIONS];

/**
 * A row that can't be dragged: `always_on` columns (shown with a lock, can't be hidden) and
 * columns the table fixes to an edge (`fixedIds`, e.g. a pinned-left Unit column), which can
 * still be hidden.
 */
export const isLockedColumn = (
  column: ColumnVisibilityItem,
  fixedIds: ReadonlySet<string>
) => Boolean(column.always_on) || fixedIds.has(column.id);

export const partitionColumns = (columns: ColumnVisibilityItem[]) => {
  const visible: ColumnVisibilityItem[] = [];
  const hidden: ColumnVisibilityItem[] = [];

  columns.forEach((column) => {
    if (column.disabled) return;
    if (column.checked) visible.push(column);
    else hidden.push(column);
  });

  return { visible, hidden };
};

/** Show or hide one column. A column that is shown goes to the end of the Visible list. */
export const toggleColumn = (
  columns: ColumnVisibilityItem[],
  id: string
): ColumnVisibilityItem[] => {
  const target = columns.find((column) => column.id === id);
  if (!target || target.always_on) return columns;

  if (target.checked)
    return columns.map((column) =>
      column.id === id ? { ...column, checked: false } : column
    );

  const lastVisible = columns.findLast(
    (column) => column.checked && !column.disabled && column.id !== id
  );
  return moveColumn(
    columns,
    { ...target, checked: true },
    { afterId: lastVisible?.id ?? null }
  );
};

type DragMove = {
  /** The rows of each section that can be dragged, in the order the menu lists them. */
  draggable: Record<Section, ColumnVisibilityItem[]>;
  from: { section: Section; index: number };
  to: { section: Section; index: number };
};

/**
 * Apply one drag in the Columns menu to the table's full column order. Locked rows and the
 * columns that did not move keep their place, so dragging a row never shifts a column the user
 * didn't touch.
 */
export const applyMenuDrag = (
  columns: ColumnVisibilityItem[],
  { draggable, from, to }: DragMove
): ColumnVisibilityItem[] => {
  const source = draggable[from.section];
  const moved = source[from.index];
  if (!moved) return columns;

  if (from.section === to.section) {
    const ids = source.map((column) => column.id);
    ids.splice(from.index, 1);
    ids.splice(to.index, 0, moved.id);
    return reorderWithinSlots(columns, ids);
  }

  const checked = to.section === SECTIONS.Visible;
  const next = { ...moved, checked };
  const targetRows = draggable[to.section];
  const before = targetRows.at(to.index);
  if (before) return moveColumn(columns, next, { beforeId: before.id });

  const last = targetRows.at(-1);
  if (last) return moveColumn(columns, next, { afterId: last.id });

  // The target list has no draggable rows yet. A newly hidden column keeps its slot; a newly
  // shown one goes after the shown (locked) columns.
  if (!checked)
    return columns.map((column) => (column.id === moved.id ? next : column));
  const lastShown = columns.findLast(
    (column) => column.checked && !column.disabled && column.id !== moved.id
  );
  return moveColumn(columns, next, { afterId: lastShown?.id ?? null });
};

export const matchesSearch = (column: ColumnVisibilityItem, search: string) => {
  if (!search) return true;
  return column.label.toLowerCase().includes(search.toLowerCase());
};
